import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  Renderer2,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { take } from 'rxjs';
import { AuthStore, LocalStorageJwtService, ThemeStore } from '~/shared-common';
import { selectCurrentRoute } from '~/shared-core/router';
import { environment } from '../environments/environment';

const USER_THEME_STORAGE_KEY = 'user_theme';

@Component({
  standalone: true,
  imports: [RouterModule, CommonModule, ToastModule],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  $title = 'Worktraq 2024';

  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly localStorageJwtService = inject(LocalStorageJwtService);
  private readonly authStore = inject(AuthStore);
  private readonly themeStore = inject(ThemeStore);
  currentRoute$ = this.store.select(selectCurrentRoute);

  $error = this.themeStore.error;

  constructor(
    private cd: ChangeDetectorRef,
    private renderer2: Renderer2,
  ) {
    effect(() => {
      const error = this.$error();
      if (error !== null) {
        this.messageService.add({
          severity: 'error',
          detail: error.message,
          summary: error.title ?? 'Error',
          life: 5000,
        });
      }
    });

    effect(() => {
      const user = this.authStore.user();
      if (!user) return;

      const selectedTheme = user.user_refs?.theme ?? environment.theme;
      this.localStorageJwtService.setItemSTR(
        USER_THEME_STORAGE_KEY,
        selectedTheme,
      );
      this.themeStore.setTheme(selectedTheme, this.renderer2);
    });
  }

  ngOnInit(): void {
    this.authStore.hydrateSwitchSession();
    this.localStorageJwtService
      .getItem()
      .pipe(take(1))
      .subscribe((token) => {
        if (token) {
          const cachedTheme =
            this.localStorageJwtService.getItemSTR(USER_THEME_STORAGE_KEY) ??
            environment.theme;
          this.themeStore.setTheme(cachedTheme, this.renderer2);
          this.authStore.getUser();
          return;
        }
        const cachedTheme =
          this.localStorageJwtService.getItemSTR(USER_THEME_STORAGE_KEY) ??
          environment.theme;
        this.themeStore.setTheme(cachedTheme, this.renderer2);
      });
    /**
     * Uncomment this block below to enable switching theme based on routing
     * Temporary OPM vs new jobtraq are using same styles (theme)
     */
    /*
    this.currentRoute$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((route) => !!route),
        map((route) => route.data?.theme ?? 'default'),
        distinctUntilChanged(),
      )
      .subscribe((theme) => {
        this.themeStore.setTheme(theme, this.renderer2);
      });
    */
  }
}
